/**
 * The Finance → Payment claims moderation queue's filter + sort declaration —
 * a consumer of the generic filter toolkit (`components/filters`), mirroring
 * `studentsFilterConfig` / `usersFilterConfig`.
 *
 * Facets exposed:
 *   - status  → `status`                 multi-select over pending/confirmed/
 *                                         rejected. The page's prior default was
 *                                         the "Pending" chip, so the default
 *                                         value here is `['pending']`. The
 *                                         backend reads an *omitted* status as
 *                                         pending-only, so "All" must be sent
 *                                         literally — `allEncoding: 'all'`.
 *   - method  → `method`                 multi-select over the 5 claimed payment
 *                                         methods (cash/bank_transfer/upi_offline
 *                                         /cheque/other); allEncoding 'omit'
 *                                         (no method param = every method).
 *   - claimed → `claimedFrom`/`claimedTo` date range over the parent's recorded
 *                                         payment date (`claimedPaidAt`).
 *
 * Sorts map to the backend's allow-listed `sort` fields
 * ({createdAt, claimedPaidAt, amount}). The default (first entry) is
 * "Recently submitted" = createdAt:desc — the page's pre-existing order.
 *
 * Sortable column header: Submitted (createdAt). Both directions are registered
 * so the header can toggle desc↔asc without snapping back to the default sort
 * (an unregistered `field:dir` falls back to `defaultSort`). claimedPaidAt and
 * amount are dropdown-only single-direction sorts.
 */

import { z } from 'zod';
import type { ListFilterConfig, SortColumnBinding } from '../filters';

export const claimsFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'multi-select',
      key: 'status',
      label: 'Status',
      group: 'filters',
      // Prior page default was the "Pending" chip. The backend treats an
      // omitted status as pending-only, so the default value is pending and
      // "All" (no picks) must emit `status=all` explicitly.
      defaultValue: ['pending'],
      allEncoding: 'all',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'rejected', label: 'Rejected' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'method',
      label: 'Paid via',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'cash', label: 'Cash' },
        { value: 'bank_transfer', label: 'Bank transfer' },
        { value: 'upi_offline', label: 'UPI (offline)' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      kind: 'date-range',
      key: 'claimed',
      label: 'Claimed',
      group: 'filters',
      params: { from: 'claimedFrom', to: 'claimedTo' },
    },
  ],
  sorts: [
    { value: 'createdAt:desc', label: 'Recently submitted' },
    // Registered so the Submitted column header can toggle to ascending; an
    // unregistered `field:dir` would silently fall back to the default sort.
    { value: 'createdAt:asc', label: 'Oldest first' },
    { value: 'claimedPaidAt:desc', label: 'Payment date (newest)' },
    { value: 'amount:desc', label: 'Amount (high → low)' },
  ],
  resultNoun: ['claim', 'claims'],
};

/**
 * Column-header sort bindings, synced to the same `sort` param. Only the
 * Submitted column is bound: it sorts on `createdAt`, the one field with both
 * directions registered above (claimedPaidAt / amount stay dropdown-only).
 */
export const CLAIMS_SORT_COLUMNS: Record<'createdAt', SortColumnBinding> = {
  createdAt: { field: 'createdAt', firstClickDir: 'desc' },
};

/**
 * URL search-param contract for the Payment claims queue, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/finance/fees/claims` opens on the configured
 * defaults).
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys, so a missing key would be silently dropped on the next
 * read. In particular `claimed` is the date-range facet's PRESET key (e.g.
 * `last7`), distinct from the concrete custom bounds `claimedFrom`/`claimedTo`;
 * omitting it would stop a preset choice from sticking. Kept beside the config
 * (not in the route) so this boundary is unit-testable without loading the
 * route's React/router side effects.
 */
export const claimsSearchSchema = z.object({
  status: z.string().optional(),
  method: z.string().optional(),
  claimed: z.string().optional(),
  claimedFrom: z.string().optional(),
  claimedTo: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
